import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Text } from 'components';
import { RATE_IT_COLORS, RATE_IT_MILESTONE_COLORS } from '../constants';

/**
 * Covers the WHOLE landing, not the first card.
 *
 * The point of this screen is that there is nothing left to do on this link, so anything visible
 * behind it would be an invitation to do it — a rating bar the fan can reach is a rating bar the
 * fan will tap, and the tap would be recorded against a referral that cannot pay again. Fixed and
 * opaque is the enforcement; the copy is only the explanation.
 */
const Shell = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${RATE_IT_COLORS.CARD_BG};
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconRing = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 36px;
  background: ${RATE_IT_MILESTONE_COLORS.SECONDARY};
  border: 1px solid ${RATE_IT_MILESTONE_COLORS.MUTED}40;
`;

/**
 * The end of a referral link: this fan already completed this invite.
 *
 * Mirrors FanCam's `screens/DeepLink/components/ReferralFlow/ReferralErrorState` — icon ring,
 * title, body, one way out — because the two are the same moment in two flows and should not
 * feel like different products. It does NOT import that component or its theme: reaching into
 * another screen's private tokens is the cross-feature dependency this repo avoids, and Rate It
 * already keeps its own copy of the referral palette for exactly that reason (see
 * `RATE_IT_MILESTONE_COLORS`). New keys rather than FanCam's `referral.alreadyCompleted*` for the
 * same reason plus one more: that copy says "challenge", and it has no wording for a completion
 * that paid nothing.
 *
 * PER LINK, not per fan. A fan who finished one invite and was sent another meets this on the
 * first and the ordinary feed on the second — which is the whole point of referrals being keyed
 * on the link.
 *
 * `points` is what was ACTUALLY paid, and the three states are all real. A number says what they
 * earned; `0` means the invite completed but paid nothing, because the reward was priced at zero
 * when it completed, and must not be rendered as "earned 0 points"; `undefined` is a referral
 * completed before the figure was recorded, where the honest answer is to claim no figure at all.
 */
const RateItReferralBlocked = ({ senderName, points }) => {
  const { t } = useTranslation();

  const body = !senderName
    ? t('tiri.rateIt.referral.completedBodyNoSender', 'You already completed this invite.')
    : points
      ? t('tiri.rateIt.referral.completedBody', {
          defaultValue: 'You and @{{name}} already earned {{points, number}} points together.',
          name: senderName,
          points
        })
      : t('tiri.rateIt.referral.completedBodyNoPoints', {
          defaultValue: 'You and @{{name}} already completed this invite together.',
          name: senderName
        });

  return (
    <Shell>
      <Card>
        <IconRing>🎉</IconRing>
        <Text h3 bold col={RATE_IT_MILESTONE_COLORS.WHITE} marg="0 0 8px">
          {t('tiri.rateIt.referral.completedTitle', "You've already completed this invite")}
        </Text>
        <Text b1 col={RATE_IT_MILESTONE_COLORS.MUTED} marg="0 0 24px">
          {body}
        </Text>
      </Card>
    </Shell>
  );
};

export default RateItReferralBlocked;
